import React from 'react';
import { ReportData } from '../../types/report.types';

interface KeyFindingsEditorProps {
  reportData: ReportData;
  onFindingChange: (index: number, field: string, value: string) => void;
  onAddFinding: () => void;
  onRemoveFinding: (index: number) => void;
}

type SeverityCategory = 'Critical' | 'High' | 'Medium' | 'Low' | 'Info';
type FindingStatus = 'Open' | 'Closed' | 'Business Accepts Risk' | 'Business Non-Actionable';

const KeyFindingsEditor: React.FC<KeyFindingsEditorProps> = ({
  reportData,
  onFindingChange,
  onAddFinding,
  onRemoveFinding
}) => {
  const statusColors: Record<string, string> = {
    Open: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    Closed: 'bg-slate-100 text-slate-700 border-slate-200',
    'Business Accepts Risk': 'bg-amber-50 text-amber-800 border-amber-200',
    'Business Non-Actionable': 'bg-blue-50 text-blue-800 border-blue-200'
  };

  const severityAccent: Record<string, string> = {
    Critical: 'bg-red-50 text-red-700 border-red-200',
    High: 'bg-orange-50 text-orange-700 border-orange-200',
    Medium: 'bg-amber-50 text-amber-700 border-amber-200',
    Low: 'bg-blue-50 text-blue-700 border-blue-200',
    Info: 'bg-slate-50 text-slate-700 border-slate-200',
  };

  const getNormalizedStatus = (statusStr?: string): FindingStatus => {
    const s = (statusStr || '').toLowerCase().trim();
    if (['closed', 'fixed', 'remediated', 'resolved'].includes(s)) return 'Closed';
    if (['business accepts risk', 'business accept risk', 'risk accepted'].includes(s)) return 'Business Accepts Risk';
    if (['non-actionable', 'business non-actionable', 'non actionable', 'business non actionable', 'business non actionable issue'].includes(s)) return 'Business Non-Actionable';
    return 'Open';
  };

  const handleSave = (index: number, field: string, value: string) => {
    onFindingChange(index, field, value);
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header Section */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
        <div className="flex items-center justify-between gap-3 mb-4 pb-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-1.5 h-8 bg-gradient-to-b from-blue-500 to-blue-700 rounded-full" />
            <div>
              <h2 className="text-base font-bold text-slate-900 tracking-tight">Key Findings Summary</h2>
              <p className="text-xs text-slate-500 font-medium mt-0.5">Manage and track top identified vulnerabilities</p>
            </div>
          </div>
          <button
            onClick={onAddFinding}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all shadow-sm"
          >
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
            </svg>
            Add New Finding
          </button>
        </div>

        {/* Findings Cards */}
        <div className="space-y-4">
          {reportData.reports_list && [...reportData.reports_list]
            .sort((a, b) => {
              const sevOrder: Record<string, number> = { critical: 1, high: 2, medium: 3, low: 4, info: 5 };
              const aSev = (a.severity_category || '').toLowerCase();
              const bSev = (b.severity_category || '').toLowerCase();
              return (sevOrder[aSev] || 99) - (sevOrder[bSev] || 99);
            })
            .map((finding, index) => {
              const currentStatus = getNormalizedStatus(finding.status);
              const sev = (finding.severity_category || 'Info').trim();
              const normalizedSev = sev.charAt(0).toUpperCase() + sev.slice(1).toLowerCase();
              
              const actualIndex = reportData.reports_list ? reportData.reports_list.findIndex(r => r === finding) : index;
              const targetIdx = actualIndex !== -1 ? actualIndex : index;

              const severityCounts: Record<string, number> = {};
              const sortedList = [...(reportData.reports_list || [])]
                .sort((a, b) => {
                  const sevOrder: Record<string, number> = { critical: 1, high: 2, medium: 3, low: 4, info: 5 };
                  const aSev = (a.severity_category || '').toLowerCase();
                  const bSev = (b.severity_category || '').toLowerCase();
                  return (sevOrder[aSev] || 99) - (sevOrder[bSev] || 99);
                });
              sortedList.slice(0, index + 1).forEach((f) => {
                const fSev = (f.severity_category || '').toLowerCase();
                severityCounts[fSev] = (severityCounts[fSev] || 0) + 1;
              });
              
              let abbrPrefix = 'I';
              const lowerSev = sev.toLowerCase();
              if (lowerSev === 'critical') abbrPrefix = 'C';
              else if (lowerSev === 'high') abbrPrefix = 'H';
              else if (lowerSev === 'medium') abbrPrefix = 'M';
              else if (lowerSev === 'low') abbrPrefix = 'L';
              const defaultAbbr = abbrPrefix + (severityCounts[lowerSev] || 1);

              return (
                <div key={index} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden transition-all hover:border-slate-300">
                  {/* Card Header */}
                  <div className="flex flex-wrap items-center justify-between px-5 py-3.5 border-b border-slate-200 bg-slate-50 gap-3">
                    <div className="flex items-center gap-3">
                      <span className={`px-2.5 py-1 rounded-md text-xs font-black border ${severityAccent[normalizedSev] || severityAccent.Info}`}>
                        {finding.abbreviation && finding.abbreviation.trim() !== '' ? finding.abbreviation : defaultAbbr}
                      </span>
                      <h3 className="text-sm font-bold text-slate-900">
                        {finding.title || `Finding #${String(index + 1).padStart(2, '0')}`}
                      </h3>
                      <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${statusColors[currentStatus] || 'bg-slate-100 text-slate-700'}`}>
                        {currentStatus}
                      </span>
                    </div>

                    <button
                      onClick={() => onRemoveFinding(targetIdx)}
                      className="inline-flex items-center px-3 py-1.5 text-red-600 hover:bg-red-50 text-xs font-bold rounded-lg transition-colors border border-red-200"
                    >
                      <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete
                    </button>
                  </div>

                  {/* Card Body */}
                  <div className="p-5 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Abbreviation */}
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">Abbreviation</label>
                        <input
                          type="text"
                          value={finding.abbreviation && finding.abbreviation.trim() !== '' ? finding.abbreviation : defaultAbbr}
                          onChange={(e) => handleSave(targetIdx, 'abbreviation', e.target.value)}
                          className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white font-medium text-slate-800"
                          placeholder={`e.g., ${defaultAbbr}`}
                        />
                      </div>

                      {/* Finding Title */}
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">Finding Title</label>
                        <input
                          type="text"
                          value={finding.title || ''}
                          onChange={(e) => handleSave(targetIdx, 'title', e.target.value)}
                          className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white font-medium text-slate-800"
                          placeholder="Enter finding title"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Severity */}
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">Severity</label>
                        <select
                          value={normalizedSev}
                          onChange={(e) => handleSave(targetIdx, 'severity_category', e.target.value)}
                          className="w-full px-3 py-2 text-xs font-bold border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white text-slate-800"
                        >
                          <option value="Critical">Critical</option>
                          <option value="High">High</option>
                          <option value="Medium">Medium</option>
                          <option value="Low">Low</option>
                          <option value="Info">Info</option>
                        </select>
                      </div>

                      {/* Status */}
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">Status</label>
                        <select
                          value={currentStatus}
                          onChange={(e) => handleSave(targetIdx, 'status', e.target.value)}
                          className="w-full px-3 py-2 text-xs font-bold border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white text-slate-800"
                        >
                          <option value="Open">Open</option>
                          <option value="Closed">Closed</option>
                          <option value="Business Accepts Risk">Business Accepts Risk</option>
                          <option value="Business Non-Actionable">Business Non-Actionable</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

          {/* Empty State */}
          {(!reportData.reports_list || reportData.reports_list.length === 0) && (
            <div className="text-center py-12 bg-slate-50 rounded-xl border border-dashed border-slate-200">
              <svg className="mx-auto h-12 w-12 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-3 text-sm font-bold text-slate-900">No key findings added yet</h3>
              <p className="mt-1 text-xs text-slate-500">Get started by adding your first finding.</p>
              <div className="mt-4">
                <button
                  onClick={onAddFinding}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-xs font-bold rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm"
                >
                  <svg className="-ml-1 mr-1.5 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
                  </svg>
                  Add Key Finding
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default KeyFindingsEditor;